import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useAccount, useMsal } from '@azure/msal-react';
import { AccountInfo, AuthenticationResult, InteractionStatus } from '@azure/msal-browser';
import { microsoftClientId, microsoftRequest, msalConfig } from '@shared/services/EnvironmentVariables';
import { GetCurrentAuthType } from '@shared/utils/AuthUtils';
import { agent } from '@shared/services/agent';

interface LogEntry {
  timestamp: Date;
  level: 'info' | 'warn' | 'error' | 'success';
  message: string;
  detail?: string;
}

export default function MsalDebugPage() {
  const { instance, accounts, inProgress } = useMsal();
  const activeAccount = useAccount(instance.getActiveAccount() ?? undefined);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [silentTokenResult, setSilentTokenResult] = useState<string | null>(null);
  const [calendarScopeResult, setCalendarScopeResult] = useState<string | null>(null);
  const [isTestingToken, setIsTestingToken] = useState(false);
  const [isSendingReport, setIsSendingReport] = useState(false);
  const [reportSent, setReportSent] = useState(false);

  const addLog = (level: LogEntry['level'], message: string, detail?: string) => {
    setLogs(prev => [...prev, { timestamp: new Date(), level, message, detail }]);
  };

  useEffect(() => {
    addLog('info', 'MSAL Debug Page loaded');
    addLog('info', `MSAL inProgress status: ${inProgress}`);
    addLog('info', `Total accounts in cache: ${accounts.length}`);
    addLog('info', `Active account: ${instance.getActiveAccount()?.username ?? 'none'}`);
    addLog('info', `Auth type (current): ${GetCurrentAuthType() ?? 'not set'}`);
    addLog('info', `Client ID configured: ${microsoftClientId ? 'yes' : 'NO - MISSING'}`);
    if (!microsoftClientId) {
      addLog('error', 'VITE_MICROSOFT_CLIENT_ID is not set. MSAL will not work.');
    }
  }, []);

  const testSilentToken = async () => {
    setIsTestingToken(true);
    setSilentTokenResult(null);
    addLog('info', 'Attempting acquireTokenSilent...');

    const account = instance.getActiveAccount() ?? accounts[0];
    if (!account) {
      addLog('error', 'No account available for silent token request');
      setIsTestingToken(false);
      return;
    }

    try {
      const result: AuthenticationResult = await instance.acquireTokenSilent({
        ...microsoftRequest.login,
        redirectUri: `${window.location.origin}/blank.html`,
        account,
        forceRefresh: false,
      });

      addLog('success', 'acquireTokenSilent succeeded');
      addLog('info', `Token expires: ${result.expiresOn?.toISOString() ?? 'unknown'}`);
      addLog('info', `Scopes granted: ${result.scopes.join(', ')}`);
      addLog('info', `ID token claims (sub): ${(result.idTokenClaims as any)?.sub ?? 'n/a'}`);
      addLog('info', `Token source: ${result.fromCache ? 'cache' : 'network'}`);

      setSilentTokenResult(JSON.stringify({
        expiresOn: result.expiresOn,
        scopes: result.scopes,
        fromCache: result.fromCache,
        tokenType: result.tokenType,
        idTokenClaimsKeys: Object.keys(result.idTokenClaims ?? {}),
      }, null, 2));
    } catch (e: any) {
      addLog('error', `acquireTokenSilent failed: ${e.errorCode ?? 'unknown'}`, e.message);
      setSilentTokenResult(`Error: ${e.errorCode}\n${e.message}`);
    } finally {
      setIsTestingToken(false);
    }
  };

  const testCalendarScopes = async () => {
    setIsTestingToken(true);
    addLog('info', 'Attempting acquireTokenSilent with calendar scopes...');

    const account = instance.getActiveAccount() ?? accounts[0];
    if (!account) {
      addLog('error', 'No account available for calendar scope test');
      setIsTestingToken(false);
      return;
    }

    try {
      const result = await instance.acquireTokenSilent({
        ...microsoftRequest.login,
        scopes: microsoftRequest.calendarScopes,
        redirectUri: `${window.location.origin}/blank.html`,
        account,
        forceRefresh: false,
      });

      addLog('success', 'Calendar scope token acquired');
      addLog('info', `Calendar scopes granted: ${result.scopes.join(', ')}`);
      setCalendarScopeResult(JSON.stringify({ scopes: result.scopes, expiresOn: result.expiresOn }, null, 2));
    } catch (e: any) {
      addLog('error', `Calendar scope request failed: ${e.errorCode ?? 'unknown'}`, e.message);
      setCalendarScopeResult(`Error: ${e.errorCode}\n${e.message}`);
      if (e.errorMessage?.includes('AADSTS65001')) {
        addLog('warn', 'User has not consented to calendar scopes (AADSTS65001)');
      }
    } finally {
      setIsTestingToken(false);
    }
  };

  const forceRefreshToken = async () => {
    setIsTestingToken(true);
    addLog('info', 'Attempting acquireTokenSilent with forceRefresh=true...');

    const account = instance.getActiveAccount() ?? accounts[0];
    if (!account) {
      addLog('error', 'No account available');
      setIsTestingToken(false);
      return;
    }

    try {
      const result = await instance.acquireTokenSilent({
        ...microsoftRequest.login,
        redirectUri: `${window.location.origin}/blank.html`,
        account,
        forceRefresh: true,
      });

      addLog('success', 'Force refresh succeeded');
      addLog('info', `New token expires: ${result.expiresOn?.toISOString() ?? 'unknown'}`);
    } catch (e: any) {
      addLog('error', `Force refresh failed: ${e.errorCode ?? 'unknown'}`, e.message);
    } finally {
      setIsTestingToken(false);
    }
  };

  const msalLogin = async () => {
    addLog('info', 'Attempting MSAL login via redirect...');
    try {
      await instance.loginPopup({
        ...microsoftRequest.login,
        redirectUri: `${window.location.origin}/debug/msal`,
      });
      addLog('success', `Login succeeded. Active account: ${instance.getActiveAccount()?.username ?? 'none'}`);
    } catch (e: any) {
      addLog('error', `Login failed: ${e.errorCode ?? 'unknown'}`, e.message);
    }
  };

  const clearLogs = () => setLogs([]);

  const sendReport = async () => {
    setIsSendingReport(true);
    setReportSent(false);
    addLog('info', 'Sending debug report to server...');

    const activeAcc = instance.getActiveAccount();
    const payload = {
      timestamp: new Date().toISOString(),
      username: activeAcc?.username ?? 'none',
      authType: GetCurrentAuthType() ?? 'not set',
      interactionStatus: inProgress,
      accountCount: accounts.length,
      activeAccount: activeAcc ? {
        username: activeAcc.username,
        name: activeAcc.name,
        tenantId: activeAcc.tenantId,
        homeAccountId: activeAcc.homeAccountId,
        environment: activeAcc.environment,
      } : null,
      accounts: accounts.map((a: AccountInfo) => ({
        username: a.username,
        name: a.name,
        tenantId: a.tenantId,
        homeAccountId: a.homeAccountId,
        environment: a.environment,
      })),
      localStorageState: {
        accessToken: localStorage.getItem('accessToken') ? 'present' : 'absent',
        idToken: localStorage.getItem('idToken') ? 'present' : 'absent',
        authToken: localStorage.getItem('authToken') ? 'present' : 'absent',
        tokenExpires: localStorage.getItem('tokenExpires'),
        type: localStorage.getItem('type'),
        redirect: localStorage.getItem('redirect'),
        attempt: localStorage.getItem('attempt'),
      },
      config: {
        clientId: microsoftClientId ? `${microsoftClientId.substring(0, 8)}...` : 'NOT SET',
        authority: msalConfig.auth.authority,
        redirectUri: msalConfig.auth.redirectUri,
        cacheLocation: msalConfig.cache.cacheLocation,
        loginScopes: microsoftRequest.login.scopes,
        calendarScopes: microsoftRequest.calendarScopes,
      },
      tokenTestResult: silentTokenResult,
      calendarScopeResult: calendarScopeResult,
      logs: logs.map(l => ({
        time: l.timestamp.toISOString(),
        level: l.level,
        message: l.message,
        detail: l.detail,
      })),
    };

    try {
      await agent.requests.post('/api/debug/msal', payload, true);
      addLog('success', 'Debug report sent successfully');
      setReportSent(true);
    } catch (e: any) {
      addLog('error', `Failed to send report: ${e?.message ?? 'unknown error'}`);
    } finally {
      setIsSendingReport(false);
    }
  };

  const localStorageAuthState = {
    accessToken: localStorage.getItem('accessToken') ? 'present' : 'absent',
    idToken: localStorage.getItem('idToken') ? 'present' : 'absent',
    authToken: localStorage.getItem('authToken') ? 'present' : 'absent',
    tokenExpires: localStorage.getItem('tokenExpires'),
    type: localStorage.getItem('type'),
    redirect: localStorage.getItem('redirect'),
    attempt: localStorage.getItem('attempt'),
  };

  return (
    <Container>
      <h1>MSAL Debug</h1>

      <Section>
        <SectionTitle>Configuration</SectionTitle>
        <InfoGrid>
          <InfoLabel>Client ID</InfoLabel>
          <InfoValue>{microsoftClientId ? `${microsoftClientId.substring(0, 8)}...` : <ErrorText>NOT SET</ErrorText>}</InfoValue>

          <InfoLabel>Authority</InfoLabel>
          <InfoValue>{msalConfig.auth.authority}</InfoValue>

          <InfoLabel>Redirect URI</InfoLabel>
          <InfoValue>{msalConfig.auth.redirectUri}</InfoValue>

          <InfoLabel>Cache Location</InfoLabel>
          <InfoValue>{msalConfig.cache.cacheLocation}</InfoValue>

          <InfoLabel>Login Scopes</InfoLabel>
          <InfoValue>{microsoftRequest.login.scopes.join(', ')}</InfoValue>

          <InfoLabel>Calendar Scopes</InfoLabel>
          <InfoValue>{microsoftRequest.calendarScopes.join(', ')}</InfoValue>
        </InfoGrid>
      </Section>

      <Section>
        <SectionTitle>MSAL State</SectionTitle>
        <InfoGrid>
          <InfoLabel>Interaction Status</InfoLabel>
          <InfoValue>
            <StatusBadge status={inProgress === InteractionStatus.None ? 'ok' : 'busy'}>
              {inProgress}
            </StatusBadge>
          </InfoValue>

          <InfoLabel>Current Auth Type</InfoLabel>
          <InfoValue>{GetCurrentAuthType() ?? 'not set'}</InfoValue>

          <InfoLabel>Accounts in Cache</InfoLabel>
          <InfoValue>{accounts.length}</InfoValue>

          <InfoLabel>Active Account</InfoLabel>
          <InfoValue>{instance.getActiveAccount()?.username ?? 'none'}</InfoValue>
        </InfoGrid>
      </Section>

      <Section>
        <SectionTitle>Accounts ({accounts.length})</SectionTitle>
        {accounts.length === 0 ? (
          <EmptyState>No accounts in MSAL cache</EmptyState>
        ) : (
          accounts.map((account: AccountInfo, i: number) => (
            <AccountCard key={account.homeAccountId} isActive={account.homeAccountId === instance.getActiveAccount()?.homeAccountId}>
              <AccountHeader>
                Account {i + 1}
                {account.homeAccountId === instance.getActiveAccount()?.homeAccountId && <ActiveBadge>Active</ActiveBadge>}
              </AccountHeader>
              <InfoGrid>
                <InfoLabel>Username</InfoLabel>
                <InfoValue>{account.username}</InfoValue>

                <InfoLabel>Name</InfoLabel>
                <InfoValue>{account.name ?? 'n/a'}</InfoValue>

                <InfoLabel>Tenant ID</InfoLabel>
                <InfoValue style={{ fontSize: '12px' }}>{account.tenantId}</InfoValue>

                <InfoLabel>Home Account ID</InfoLabel>
                <InfoValue style={{ fontSize: '12px' }}>{account.homeAccountId}</InfoValue>

                <InfoLabel>Environment</InfoLabel>
                <InfoValue>{account.environment}</InfoValue>

                <InfoLabel>Local Account ID</InfoLabel>
                <InfoValue style={{ fontSize: '12px' }}>{account.localAccountId}</InfoValue>
              </InfoGrid>
              {account.homeAccountId !== instance.getActiveAccount()?.homeAccountId && (
                <SetActiveButton onClick={() => { instance.setActiveAccount(account); addLog('info', `Set active account to: ${account.username}`); }}>
                  Set as Active
                </SetActiveButton>
              )}
            </AccountCard>
          ))
        )}
      </Section>

      <Section>
        <SectionTitle>localStorage Auth State</SectionTitle>
        <InfoGrid>
          {Object.entries(localStorageAuthState).map(([key, value]) => (
            <React.Fragment key={key}>
              <InfoLabel>{key}</InfoLabel>
              <InfoValue>{value ?? 'null'}</InfoValue>
            </React.Fragment>
          ))}
        </InfoGrid>
      </Section>

      <Section>
        <SectionTitle>Token Tests</SectionTitle>
        <ButtonRow>
          <LoginButton onClick={msalLogin} disabled={inProgress !== InteractionStatus.None}>
            MSAL Login
          </LoginButton>
          <TestButton onClick={testSilentToken} disabled={isTestingToken}>
            Test Silent Token
          </TestButton>
          <TestButton onClick={testCalendarScopes} disabled={isTestingToken}>
            Test Calendar Scopes
          </TestButton>
          <TestButton onClick={forceRefreshToken} disabled={isTestingToken}>
            Force Token Refresh
          </TestButton>
        </ButtonRow>
        {silentTokenResult && (
          <Pre>{silentTokenResult}</Pre>
        )}
      </Section>

      <Section>
        <SectionTitle>Send Report</SectionTitle>
        <ButtonRow>
          <SendReportButton onClick={sendReport} disabled={isSendingReport}>
            {isSendingReport ? 'Sending...' : reportSent ? 'Report Sent' : 'Send Debug Report'}
          </SendReportButton>
        </ButtonRow>
      </Section>

      <Section>
        <SectionTitleRow>
          <SectionTitle>Log</SectionTitle>
          <ClearButton onClick={clearLogs}>Clear</ClearButton>
        </SectionTitleRow>
        <LogContainer>
          {logs.length === 0 ? (
            <EmptyState>No log entries</EmptyState>
          ) : (
            logs.map((entry, i) => (
              <LogLine key={i} level={entry.level}>
                <LogTime>{entry.timestamp.toLocaleTimeString()}</LogTime>
                <LogLevel level={entry.level}>{entry.level.toUpperCase()}</LogLevel>
                <LogMessage>
                  {entry.message}
                  {entry.detail && <LogDetail>{entry.detail}</LogDetail>}
                </LogMessage>
              </LogLine>
            ))
          )}
        </LogContainer>
      </Section>
    </Container>
  );
}

const Container = styled.div`
  max-width: 900px;
  margin: 40px auto;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const Section = styled.div`
  margin-bottom: 24px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 12px 0;
  color: #111827;
`;

const SectionTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 6px 12px;
  font-size: 14px;
`;

const InfoLabel = styled.div`
  color: #6b7280;
  font-weight: 500;
`;

const InfoValue = styled.div`
  color: #111827;
  word-break: break-all;
`;

const ErrorText = styled.span`
  color: #dc2626;
  font-weight: 600;
`;

const StatusBadge = styled.span<{ status: 'ok' | 'busy' }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background: ${p => p.status === 'ok' ? '#d1fae5' : '#fef3c7'};
  color: ${p => p.status === 'ok' ? '#065f46' : '#92400e'};
`;

const AccountCard = styled.div<{ isActive: boolean }>`
  padding: 12px;
  margin-bottom: 8px;
  background: white;
  border: 1px solid ${p => p.isActive ? '#3b82f6' : '#e5e7eb'};
  border-radius: 6px;
`;

const AccountHeader = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActiveBadge = styled.span`
  font-size: 11px;
  padding: 2px 6px;
  background: #dbeafe;
  color: #1d4ed8;
  border-radius: 4px;
  font-weight: 600;
`;

const SetActiveButton = styled.button`
  margin-top: 8px;
  padding: 4px 12px;
  font-size: 12px;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  cursor: pointer;
  &:hover { background: #e5e7eb; }
`;

const EmptyState = styled.div`
  color: #9ca3af;
  font-style: italic;
  font-size: 14px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const TestButton = styled.button`
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  background: #1d4ed8;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  &:hover { background: #1e40af; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const LoginButton = styled.button`
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  background: #7c3aed;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  &:hover { background: #6d28d9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SendReportButton = styled.button`
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  background: #059669;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  &:hover { background: #047857; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ClearButton = styled.button`
  padding: 4px 12px;
  font-size: 12px;
  background: transparent;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  cursor: pointer;
  color: #6b7280;
  &:hover { background: #f3f4f6; }
`;

const Pre = styled.pre`
  margin-top: 12px;
  padding: 12px;
  background: #1f2937;
  color: #d1d5db;
  border-radius: 6px;
  font-size: 12px;
  overflow-x: auto;
  white-space: pre-wrap;
`;

const LogContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;
  font-size: 13px;
`;

const LogLine = styled.div<{ level: string }>`
  display: flex;
  gap: 8px;
  padding: 4px 0;
  border-bottom: 1px solid #f3f4f6;
  align-items: flex-start;
`;

const LogTime = styled.span`
  color: #9ca3af;
  font-size: 12px;
  white-space: nowrap;
  min-width: 80px;
`;

const LogLevel = styled.span<{ level: string }>`
  font-size: 11px;
  font-weight: 700;
  min-width: 55px;
  color: ${p => {
    switch (p.level) {
      case 'error': return '#dc2626';
      case 'warn': return '#d97706';
      case 'success': return '#059669';
      default: return '#6b7280';
    }
  }};
`;

const LogMessage = styled.span`
  color: #111827;
  flex: 1;
`;

const LogDetail = styled.div`
  color: #6b7280;
  font-size: 12px;
  margin-top: 2px;
  word-break: break-all;
`;
